import { useState } from 'react';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import AddIcon from '@mui/icons-material/Add';
import { useQuery } from '@tanstack/react-query';
import { PurchaseOrderSchema, type SupplierDto } from '@taqon/contracts';
import { Link as RouterLink, useSearchParams } from 'react-router';
import { getPage } from '../../shared/api/client';
import { hasCapability, useSession } from '../../shared/session/session';
import { StatusChip } from '../../shared/ui/forms';
import { ExportButton } from '../../shared/export/ExportButton';
import { exportHref } from '../../shared/export/export-href';
import { SearchFilterBar } from '../../shared/ui/filters';
import { EmptyState, ErrorState, LoadingState, PageHeader, Section } from '../../shared/ui/page';
import { Pager } from '../crm/common';
import { RecordLink } from '../finance/detail';
import { SupplierPicker } from '../finance/lookups';
import { UrlDimensionNote } from '../reporting/dimensions';
import { OriginChip, PO_API, PO_STATUS_LABELS, PO_TYPE } from './purchasing';

const PAGE_SIZE = 50;

/**
 * Purchase orders (FRD-PRC-032, PRC-034, PRC-064): tracked by status, origin, supplier and date,
 * with the **outstanding orders** view — issued or partially received orders with something still
 * to receive. Totals and outstanding values are summed on the server.
 */
export function PurchaseOrdersPage() {
  const session = useSession();
  const canCreate = hasCapability(session.data, `${PO_TYPE}:create`);
  const canSeeSuppliers = hasCapability(session.data, 'procurement.supplier:view');
  const [params, setParams] = useSearchParams();
  const [search, setSearch] = useState(params.get('search') ?? '');
  const [supplier, setSupplier] = useState<SupplierDto | null>(null);
  const filters = {
    status: params.get('status') ?? '',
    origin: params.get('origin') ?? '',
    supplier: params.get('supplier') ?? '',
    outstanding: params.get('outstanding') ?? '',
    from: params.get('from') ?? '',
    to: params.get('to') ?? '',
    // The business date the order was issued (FRD-PRC-031); the dashboards link here.
    issued_from: params.get('issued_from') ?? '',
    issued_to: params.get('issued_to') ?? '',
    search: params.get('search') ?? '',
  };
  const page = Math.max(1, Number.parseInt(params.get('page') ?? '1', 10) || 1);
  const set = (key: keyof typeof filters | 'page', value: string) => {
    const next = new URLSearchParams(params);
    if (value === '') next.delete(key);
    else next.set(key, value);
    if (key !== 'page') next.delete('page');
    setParams(next);
  };
  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(PAGE_SIZE),
    sort: '-document_no',
  });
  for (const [key, value] of [
    ['status', filters.status],
    ['origin', filters.origin],
    // The page's own supplier choice, else a dashboard's supplier dimension (FRD-RPT-010).
    ['supplier_id', filters.supplier === '' ? (params.get('supplier_id') ?? '') : filters.supplier],
    ['outstanding', filters.outstanding],
    ['po_date_from', filters.from],
    ['po_date_to', filters.to],
    ['issued_from', filters.issued_from],
    ['issued_to', filters.issued_to],
    ['search', filters.search],
  ] as const) {
    if (value !== '') query.set(`filter[${key}]`, value);
  }
  const orders = useQuery({
    queryKey: ['procurement', 'purchase-orders', query.toString()],
    queryFn: () => getPage(`${PO_API}?${query.toString()}`, PurchaseOrderSchema),
  });

  return (
    <Stack spacing={3}>
      <PageHeader
        title="Purchase orders"
        description="Orders from approved purchase requests, and the controlled emergency exception."
        actions={
          <>
            <ExportButton
              recordType="procurement.purchase_order"
              href={exportHref(PO_API, query)}
            />
            {canCreate && (
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                component={RouterLink}
                to="/procurement/purchase-orders/new"
              >
                New purchase order
              </Button>
            )}
          </>
        }
      />
      <UrlDimensionNote keys={['supplier_id']} />
      <SearchFilterBar
        aria-label="Purchase order filters"
        label="Search by number"
        value={search}
        onChange={setSearch}
        onSubmit={() => {
          set('search', search.trim());
        }}
      >
        <TextField
          select
          size="small"
          label="Status"
          // "All" is the empty value; without these the select would show it blank.
          slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
          value={filters.status}
          onChange={(e) => {
            set('status', e.target.value);
          }}
        >
          <MenuItem value="">All</MenuItem>
          {Object.entries(PO_STATUS_LABELS).map(([value, label]) => (
            <MenuItem key={value} value={value}>
              {label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Origin"
          value={filters.origin}
          onChange={(e) => {
            set('origin', e.target.value);
          }}
        >
          <MenuItem value="">All</MenuItem>
          <MenuItem value="purchase_request">From purchase request</MenuItem>
          <MenuItem value="emergency">Emergency</MenuItem>
        </TextField>
        {canSeeSuppliers && (
          <SupplierPicker
            value={supplier}
            required={false}
            onChange={(next) => {
              setSupplier(next);
              set('supplier', next?.id ?? '');
            }}
          />
        )}
        <TextField
          size="small"
          type="date"
          label="Ordered from"
          value={filters.from}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            set('from', e.target.value);
          }}
        />
        <TextField
          size="small"
          type="date"
          label="Ordered to"
          value={filters.to}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            set('to', e.target.value);
          }}
        />
        <TextField
          size="small"
          type="date"
          label="Issued from"
          value={filters.issued_from}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            set('issued_from', e.target.value);
          }}
        />
        <TextField
          size="small"
          type="date"
          label="Issued to"
          value={filters.issued_to}
          slotProps={{ inputLabel: { shrink: true } }}
          onChange={(e) => {
            set('issued_to', e.target.value);
          }}
        />
        <FormControlLabel
          control={
            <Checkbox
              checked={filters.outstanding === 'true'}
              onChange={(e) => {
                set('outstanding', e.target.checked ? 'true' : '');
              }}
            />
          }
          label="Outstanding orders only"
        />
      </SearchFilterBar>
      <Section title="Orders">
        {orders.isPending && <LoadingState label="Loading purchase orders" />}
        {orders.isError && <ErrorState message={orders.error.message} />}
        {orders.data?.items.length === 0 && (
          <EmptyState title="No purchase orders" detail="None matches these filters." />
        )}
        {orders.data !== undefined && orders.data.items.length > 0 && (
          <>
            <Table size="small" aria-label="Purchase orders">
              <TableHead>
                <TableRow>
                  <TableCell>Number</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Supplier</TableCell>
                  <TableCell>Origin</TableCell>
                  <TableCell align="right">Total incl. VAT</TableCell>
                  <TableCell align="right">Outstanding</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {orders.data.items.map((o) => (
                  <TableRow key={o.id}>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      <RecordLink to={`/procurement/purchase-orders/${o.id}`}>
                        {o.documentNo}
                      </RecordLink>
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{o.poDate}</TableCell>
                    <TableCell>{o.supplierLabel ?? '—'}</TableCell>
                    <TableCell>
                      <OriginChip origin={o.origin} />
                    </TableCell>
                    <TableCell align="right">{o.grossTotal}</TableCell>
                    <TableCell align="right">{o.outstandingValue}</TableCell>
                    <TableCell>
                      <StatusChip status={o.status} />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <Pager
              page={page}
              pageSize={PAGE_SIZE}
              total={orders.data.total}
              noun="orders"
              onPage={(next) => {
                set('page', String(next));
              }}
            />
          </>
        )}
      </Section>
    </Stack>
  );
}
